import { useState, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail, Phone, Plus, Trash2, Link2, Unlink, Pencil, Check, X,
  ChevronDown, ChevronUp, AlertCircle, Loader2, Filter, User,
} from 'lucide-react'
import * as Switch from '@radix-ui/react-switch'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { notificationContactsApi } from '@/api/endpoints'
import type { NotificationContact, ContactChannel, ContactCameraLink } from '@/types'
import { SectionSkeleton, SectionError, SaveStatus } from './SectionState'
import { contactsQuery, camerasQuery } from './prefetch'

// Extract a readable error message from an axios error response
function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
  if (!data) return fallback
  for (const key of ['contact_value', 'channel', 'label', 'detail', 'non_field_errors']) {
    const val = data[key]
    if (Array.isArray(val) && val.length > 0) return String(val[0])
    if (typeof val === 'string') return val
  }
  const first = Object.values(data)[0]
  if (Array.isArray(first) && first.length > 0) return String(first[0])
  if (typeof first === 'string') return first
  return fallback
}

// ============================================================================
// Helpers
// ============================================================================

function channelIcon(channel: ContactChannel) {
  return channel === 'email'
    ? <Mail className="h-4 w-4" />
    : <Phone className="h-4 w-4" />
}

// ============================================================================
// Class Checkbox List — search + alphabetical
// ============================================================================

function ClassCheckboxList({
  availableClasses,
  selected,
  setSelected,
}: {
  availableClasses: string[]
  selected: Set<string>
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>
}) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const [search, setSearch] = useState('')
  const searchLower = search.toLowerCase().trim()

  // CP 159 — a hardcoded "common classes" list went stale the moment the fleet moved to
  // the species catalog (it was pure COCO: elephant, zebra, giraffe…). Superusers edit
  // the catalog live, so any fixed list rots. Order by what the server returns.
  const ordered = [...availableClasses].sort((a, b) => a.localeCompare(b))

  // Apply search filter
  const matchesSearch = (cls: string) => {
    if (!searchLower) return true
    const label = tc(`detectionClasses.${cls}`, { defaultValue: cls.replace(/_/g, ' ') })
    return cls.toLowerCase().includes(searchLower) || label.toLowerCase().includes(searchLower)
  }

  const filteredOrdered = ordered.filter(matchesSearch)

  const renderCheckbox = (cls: string) => (
    <label
      key={cls}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors',
        selected.has(cls) ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
      )}
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 accent-primary"
        checked={selected.has(cls)}
        onChange={() => setSelected(prev => {
          const next = new Set(prev)
          next.has(cls) ? next.delete(cls) : next.add(cls)
          return next
        })}
      />
      <span className="truncate">{tc(`detectionClasses.${cls}`, { defaultValue: cls.replace(/_/g, ' ') })}</span>
    </label>
  )

  return (
    <div>
      {/* Search field */}
      <div className="px-3 py-2 border-b">
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('classFilter.searchPlaceholder')}
          className="h-7 text-xs"
        />
      </div>

      <div className="max-h-56 overflow-y-auto p-2">
        {filteredOrdered.length > 0 ? (
          <div className="grid grid-cols-2 gap-0.5">
            {filteredOrdered.map(renderCheckbox)}
          </div>
        ) : (
          <p className="py-4 text-center text-xs text-muted-foreground">{t('classFilter.noResults')}</p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Class Filter Panel — inline collapsible, per contact-camera link
// ============================================================================

function ClassFilterPanel({
  contactId,
  cameraId,
  currentClasses,
}: {
  contactId: number
  cameraId: number
  currentClasses: string[]
}) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set(currentClasses))
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 280 })

  const handleToggle = () => {
    if (!open) {
      setSelected(new Set(currentClasses))
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const panelH = 380
        const top = spaceBelow >= panelH ? rect.bottom + 6 : rect.top - panelH - 6
        const left = Math.min(rect.left, window.innerWidth - 288)
        setPos({ top: top + window.scrollY, left: left + window.scrollX, width: 280 })
      }
    }
    setOpen(o => !o)
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (!triggerRef.current?.contains(target) && !(document.getElementById('class-filter-portal')?.contains(target))) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const { data: availData, isLoading: classesLoading } = useQuery({
    queryKey: ['camera-available-classes', contactId, cameraId],
    queryFn: () => notificationContactsApi.getCameraAvailableClasses(contactId, cameraId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  const updateMutation = useMutation({
    mutationFn: (classes: string[]) =>
      notificationContactsApi.updateCameraClasses(contactId, cameraId, classes),
    onSuccess: () => {
      const msg = selected.size === 0
        ? t('messages.filterCleared')
        : t('messages.filterActive', { count: selected.size })
      toast.success(msg)
      queryClient.invalidateQueries({ queryKey: ['contact-camera-links', contactId] })
      setOpen(false)
    },
    onError: () => toast.error(t('messages.filterFailed')),
  })

  const availableClasses = availData?.available_class_names ?? []

  const badge = currentClasses.length === 0
    ? <span className="text-[10px] italic text-muted-foreground">{t('classFilter.allBadge', { defaultValue: 'all' })}</span>
    : <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">{currentClasses.length}</span>

  const panel = open && createPortal(
    <div
      id="class-filter-portal"
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
      className="rounded-lg border bg-background shadow-xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <p className="text-sm font-semibold">{t('classFilter.title')}</p>
          <p className="text-[11px] text-muted-foreground">{t('classFilter.hint')}</p>
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">✕</button>
      </div>

      {classesLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : availableClasses.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-muted-foreground">{t('push.noClassesConfigured', { ns: 'settings' })}</p>
      ) : (
        <>
          {/* Quick select bar */}
          <div className="flex items-center gap-1 border-b bg-muted/30 px-3 py-2">
            <span className="mr-1 text-[11px] text-muted-foreground">{t('classFilter.quick')}</span>
            <button
              className="rounded bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20"
              onClick={() => setSelected(new Set(availableClasses))}
            >{t('classFilter.all', { count: availableClasses.length })}</button>
            <button
              className="rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
              onClick={() => setSelected(new Set())}
            >{t('classFilter.none')}</button>
            {selected.size > 0 && (
              <span className="ml-auto text-[11px] text-primary">{t('classFilter.selected', { count: selected.size })}</span>
            )}
          </div>

          {/* Search + class list */}
          <ClassCheckboxList
            availableClasses={availableClasses}
            selected={selected}
            setSelected={setSelected}
          />
        </>
      )}

      {/* Footer */}
      <div className="flex justify-end gap-2 border-t px-4 py-3">
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setOpen(false)}>
          {tc('actions.cancel')}
        </Button>
        <Button
          size="sm"
          className="h-8 text-xs"
          onClick={() => updateMutation.mutate([...selected])}
          disabled={updateMutation.isPending || classesLoading}
        >
          {updateMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : t('classFilter.saveFilter')}
        </Button>
      </div>
    </div>,
    document.body
  )

  return (
    <div>
      <button
        ref={triggerRef}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
        onClick={handleToggle}
        title={t('classFilter.configureFilter')}
      >
        <Filter className="h-3 w-3" />
        {badge}
      </button>
      {panel}
    </div>
  )
}

// ============================================================================
// Camera Link Manager
// ============================================================================

function CameraLinkManager({ contact }: { contact: NotificationContact }) {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  // Item 307 — the camera list is fetched only where it is needed: when this contact's camera links are
  // opened (shared key, so a list the app already loaded shows at once). The contacts never wait for it.
  const camerasQ = useQuery({ ...camerasQuery, enabled: open })
  const cameras = camerasQ.data ?? []

  const linkedIds = new Set(contact.linked_camera_ids)
  const unlinkedCameras = cameras.filter(c => !linkedIds.has(c.id))
  const linkedCameras = cameras.filter(c => linkedIds.has(c.id))

  // Fetch full link objects (with alert_class_names) when panel is open
  const { data: cameraLinks = [] } = useQuery<ContactCameraLink[]>({
    queryKey: ['contact-camera-links', contact.id],
    queryFn: () => notificationContactsApi.getCameras(contact.id),
    enabled: open,
  })
  const linkByCameraId = new Map(cameraLinks.map(l => [l.camera_id, l]))

  const linkMutation = useMutation({
    mutationFn: (cameraId: number) =>
      notificationContactsApi.linkCameras(contact.id, [cameraId]),
    onSuccess: () => {
      toast.success(t('messages.linked'))
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-camera-links', contact.id] })
    },
    onError: () => toast.error(t('messages.linkFailed')),
  })

  const unlinkMutation = useMutation({
    mutationFn: (cameraId: number) =>
      notificationContactsApi.unlinkCamera(contact.id, cameraId),
    onSuccess: () => {
      toast.warning(t('messages.unlinked'))
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-camera-links', contact.id] })
    },
    onError: () => toast.error(t('messages.unlinkFailed')),
  })

  return (
    <div className="mt-3">
      <button
        className="flex w-full items-center justify-between text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(o => !o)}
      >
        <span className="flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" />
          {t('contacts.linkedCameras', { count: linkedIds.size })}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {open && camerasQ.isPending && (
        <div className="mt-2"><SectionSkeleton rows={1} /></div>
      )}

      {open && camerasQ.isError && !camerasQ.data && (
        <div className="mt-2"><SectionError onRetry={() => camerasQ.refetch()} /></div>
      )}

      {open && camerasQ.data && (
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-1">
          {/* Currently linked */}
          {linkedCameras.length > 0 && (
            <div className="space-y-1">
              {linkedCameras.map(cam => {
                const link = linkByCameraId.get(cam.id)
                return (
                  <div key={cam.id} className="rounded-md bg-muted/50 px-3 py-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate">{cam.name}</span>
                      <div className="flex items-center gap-1">
                        {link && (
                          <ClassFilterPanel
                            contactId={contact.id}
                            cameraId={cam.id}
                            currentClasses={link.alert_class_names}
                          />
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-xs text-muted-foreground hover:text-destructive"
                          onClick={() => unlinkMutation.mutate(cam.id)}
                          disabled={unlinkMutation.isPending}
                        >
                          <Unlink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Cameras to link */}
          {unlinkedCameras.length > 0 && (
            <div className="space-y-1">
              <p className="px-1 text-[11px] text-muted-foreground">{t('contacts.addCameras')}</p>
              {unlinkedCameras.map(cam => (
                <div
                  key={cam.id}
                  className="flex items-center justify-between rounded-md border border-dashed px-3 py-1.5 text-sm"
                >
                  <span className="truncate text-muted-foreground">{cam.name}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5 text-xs"
                    onClick={() => linkMutation.mutate(cam.id)}
                    disabled={linkMutation.isPending}
                  >
                    <Link2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {cameras.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('contacts.noCamerasFound')}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Critical alerts only (item 315) — one switch per saved contact
// ============================================================================
// On: this contact is emailed/texted only for critical (graffiti-spray) events on its linked cameras. It saves
// on its own. The PATCH answers only the Update serializer's fields, so the contacts are read again and the
// switch shows what the server stored; while saving it shows the value being saved and is disabled. The API
// decides who may change a contact (a superuser's shared pool included).

function CriticalOnlySwitch({ contact }: { contact: NotificationContact }) {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  const id = `contact-${contact.id}-critical-only`
  const saved = contact.critical_only ?? false // an older API does not send it: off

  const mutation = useMutation({
    mutationFn: async (critical_only: boolean) => {
      const res = await notificationContactsApi.update(contact.id, { critical_only })
      // an API without item 315 ignores the field: never report "on" when nothing was stored
      if (res.critical_only !== critical_only) throw new Error('critical_only was not stored')
      return res
    },
    onSuccess: (_data, critical_only) => {
      toast.success(critical_only ? t('messages.criticalOnlyOn') : t('messages.criticalOnlyOff'))
      // returned, so the switch stays "saving" until the contacts have been read again
      return queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
    },
    onError: () => toast.error(t('messages.criticalOnlyFailed')),
  })

  const checked = (mutation.isPending ? mutation.variables : undefined) ?? saved

  return (
    <div className="mt-3 flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="cursor-pointer text-xs font-medium">{t('contacts.criticalOnly')}</label>
        <p id={`${id}-hint`} className="text-[11px] text-muted-foreground">{t('contacts.criticalOnlyHint')}</p>
      </div>
      <SaveStatus pending={mutation.isPending} saved={mutation.isSuccess} />
      <Switch.Root
        id={id}
        checked={checked}
        onCheckedChange={(next) => mutation.mutate(next)}
        disabled={mutation.isPending}
        aria-describedby={`${id}-hint`}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
      </Switch.Root>
    </div>
  )
}

// ============================================================================
// Single Contact Card
// ============================================================================

function ContactCard({ contact }: { contact: NotificationContact }) {
  const { t } = useTranslation('settings')
  const tc = useTranslation('common').t
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editFirst, setEditFirst] = useState(contact.first_name)
  const [editLast, setEditLast] = useState(contact.last_name)
  const [editLabel, setEditLabel] = useState(contact.label)

  const deleteMutation = useMutation({
    mutationFn: () => notificationContactsApi.delete(contact.id),
    onSuccess: () => {
      toast.success(t('messages.contactRemoved'))
      setConfirmOpen(false)
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
    },
    onError: (err: unknown) => {
      setConfirmOpen(false)
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 403) {
        toast.error(
          t('messages.cannotDelete'),
          t('messages.contactOwnedBy', { name: contact.created_by ?? 'another user' })
        )
      } else {
        toast.error(t('messages.removeFailed'))
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => notificationContactsApi.update(contact.id, {
      first_name: editFirst.trim(),
      last_name: editLast.trim(),
      label: editLabel.trim(),
    }),
    onSuccess: () => {
      toast.success(t('messages.contactUpdated'))
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
    },
    onError: () => toast.error(t('messages.updateFailed')),
  })

  const startEditing = () => {
    setEditFirst(contact.first_name)
    setEditLast(contact.last_name)
    setEditLabel(contact.label)
    setEditing(true)
  }

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className={cn(
            'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
            contact.channel === 'email'
              ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
              : 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
          )}>
            {channelIcon(contact.channel)}
          </div>

          {editing ? (
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex gap-2">
                <Input value={editFirst} onChange={e => setEditFirst(e.target.value)} placeholder={t('contacts.firstNamePlaceholder')} className="h-8 text-sm" />
                <Input value={editLast} onChange={e => setEditLast(e.target.value)} placeholder={t('contacts.lastNamePlaceholder')} className="h-8 text-sm" />
              </div>
              <Input value={editLabel} onChange={e => setEditLabel(e.target.value)} placeholder={t('contacts.labelPlaceholder')} className="h-8 text-sm" />
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
                  {tc('actions.save')}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)} disabled={updateMutation.isPending}>
                  <X className="mr-1 h-3 w-3" />{tc('actions.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {(contact.first_name || contact.last_name)
                  ? <>{[contact.first_name, contact.last_name].filter(Boolean).join(' ')} <span className="font-normal text-muted-foreground">— {contact.contact_value}</span></>
                  : contact.contact_value
                }
              </p>
              <div className="flex items-center gap-2">
                {contact.label && (
                  <p className="text-xs text-muted-foreground">{contact.label}</p>
                )}
                {!contact.is_own && contact.created_by && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                    <User className="h-2.5 w-2.5" />
                    {contact.created_by}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {!editing && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" onClick={startEditing}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Trash2 className="h-4 w-4" />
              }
            </Button>
          </div>
        )}
      </div>

      {/* Item 315 — the switch first, then the cameras it applies to */}
      <CriticalOnlySwitch contact={contact} />
      <CameraLinkManager contact={contact} />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('contacts.removeTitle')}
        description={t('contacts.removeConfirm', { contact: contact.contact_value })}
        confirmLabel={t('contacts.removeButton')}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  )
}

// ============================================================================
// Add Contact Form
// ============================================================================

function AddContactForm({ onAdded }: { onAdded: () => void }) {
  const { t } = useTranslation('settings')
  const [channel, setChannel] = useState<ContactChannel>('email')
  const [value, setValue] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState('')
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: () =>
      notificationContactsApi.create({
        channel,
        contact_value: value.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        label: label.trim(),
      }),
    onSuccess: () => {
      toast.success(channel === 'email' ? t('messages.emailAdded') : t('messages.phoneAdded'))
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
      setValue('')
      setFirstName('')
      setLastName('')
      setLabel('')
      setError('')
      onAdded()
    },
    onError: (err: unknown) => {
      const msg = extractApiError(err, t('errors.genericError', { defaultValue: 'Something went wrong. Please try again.', ns: 'settings' }))
      setError(msg)
      toast.error(t('messages.addFailed'), msg)
    },
  })

  const placeholder = channel === 'email'
    ? t('contacts.emailPlaceholder')
    : t('contacts.smsPlaceholder')

  return (
    <div className="rounded-lg border border-dashed bg-muted/30 p-4">
      <p className="mb-3 text-sm font-medium">{t('contacts.addTitle')}</p>

      {/* Channel selector */}
      <div className="mb-3 flex rounded-md border overflow-hidden">
        {(['email', 'sms'] as ContactChannel[]).map(ch => (
          <button
            key={ch}
            onClick={() => { setChannel(ch); setError('') }}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 py-1.5 text-sm transition-colors',
              channel === ch
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            )}
          >
            {ch === 'email' ? <Mail className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
            {ch === 'email' ? t('contacts.email') : t('contacts.sms')}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <Input
          value={value}
          onChange={e => { setValue(e.target.value); setError('') }}
          placeholder={placeholder}
          className="h-9 text-sm"
        />
        <div className="flex gap-2">
          <Input
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder={t('contacts.firstNamePlaceholder')}
            className="h-9 text-sm"
          />
          <Input
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder={t('contacts.lastNamePlaceholder')}
            className="h-9 text-sm"
          />
        </div>
        <Input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder={t('contacts.labelPlaceholder')}
          className="h-9 text-sm"
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="h-3 w-3" />{error}
          </p>
        )}
        <Button
          className="w-full h-9"
          onClick={() => createMutation.mutate()}
          disabled={!value.trim() || createMutation.isPending}
        >
          {createMutation.isPending
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <Plus className="mr-2 h-4 w-4" />
          }
          {t('contacts.addButton')}
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// Notification Contacts Section (item 307: moved out of SettingsPage unchanged)
// ============================================================================

export function ContactsSection() {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const [showAddForm, setShowAddForm] = useState(false)

  // Item 307 — the contacts alone; the camera list is fetched by a contact's camera links when opened.
  const { data, isPending, isError, refetch } = useQuery(contactsQuery)
  const contacts = data ?? []

  const emailContacts = contacts.filter(c => c.channel === 'email')
  const smsContacts = contacts.filter(c => c.channel === 'sms')

  const handleAdded = useCallback(() => setShowAddForm(false), [])

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">{t('contacts.title')}</h2>
          <p className="text-xs text-muted-foreground">
            {t('contacts.description')}
          </p>
        </div>
        <Button
          size="sm"
          variant={showAddForm ? 'secondary' : 'default'}
          onClick={() => setShowAddForm(v => !v)}
        >
          {showAddForm ? tc('actions.cancel') : <><Plus className="mr-1.5 h-4 w-4" />{t('contacts.addContact')}</>}
        </Button>
      </div>

      {showAddForm && <AddContactForm onAdded={handleAdded} />}

      {isPending ? (
        <SectionSkeleton rows={2} />
      ) : isError && !data ? (
        <SectionError onRetry={() => refetch()} />
      ) : contacts.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center">
          <Mail className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">{t('contacts.title')}</p>
          <p className="text-xs text-muted-foreground">{t('contacts.emptyState')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Email contacts */}
          {emailContacts.length > 0 && (
            <div className="space-y-2">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Mail className="h-3.5 w-3.5" /> {t('contacts.email')} ({emailContacts.length})
              </h3>
              {emailContacts.map(contact => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          )}

          {/* SMS contacts */}
          {smsContacts.length > 0 && (
            <div className="space-y-2">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Phone className="h-3.5 w-3.5" /> {t('contacts.sms')} ({smsContacts.length})
              </h3>
              {smsContacts.map(contact => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
